/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "context/LanguageContext";

// @mui material components
import Backdrop from "@mui/material/Backdrop";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Fade from "@mui/material/Fade";
import Typography from "@mui/material/Typography";
import { keyframes, useTheme } from "@mui/material/styles";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";

// Argon Dashboard 2 MUI context
import { useArgonController } from "context";

// Firebase Auth
import { logout } from "authService";

const logoutPulse = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.85;
    transform: scale(1.04);
  }
`;

function SidenavFooter() {
  const theme = useTheme();
  const [controller] = useArgonController();
  const { miniSidenav } = controller;
  const navigate = useNavigate();

  const { t } = useLanguage();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleConfirmLogout = async () => {
    setLogoutDialogOpen(false);
    setIsLoggingOut(true);
    try {
      await logout();
      navigate("/authentication/sign-in");
    } catch (error) {
      console.error("❌ [SidenavFooter] Error al hacer logout:", error);
      navigate("/authentication/sign-in");
    }
  };

  return (
    <ArgonBox opacity={miniSidenav ? 0 : 1} sx={{ transition: "opacity 200ms linear" }}>
      <ArgonBox display="flex" flexDirection="column">
        <ArgonBox textAlign="center" mt={2}>
          <ArgonBox mb={2}>
            <ArgonBox
              component="button"
              type="button"
              onClick={() => setLogoutDialogOpen(true)}
              sx={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                padding: "6px 8px",
                margin: 0,
                font: "inherit",
                fontWeight: 700,
                fontSize: "1rem",
                lineHeight: 1.5,
                color: "#d32f2f",
                textTransform: "none",
                borderRadius: 1,
                WebkitTapHighlightColor: "transparent",
                "&:hover": {
                  color: "#800020",
                  backgroundColor: "rgba(211, 47, 47, 0.06)",
                },
                "&:focus": {
                  outline: "none",
                },
                "&:focus-visible": {
                  outline: "2px solid rgba(211, 47, 47, 0.55)",
                  outlineOffset: 2,
                  backgroundColor: "rgba(211, 47, 47, 0.06)",
                },
              }}
            >
              {t('logout.button')}
            </ArgonBox>
          </ArgonBox>
        </ArgonBox>
      </ArgonBox>

      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: { enter: 280, exit: 180 } }}
        maxWidth="sm"
        fullWidth
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <DialogTitle
          id="logout-dialog-title"
          sx={{
            pb: 2,
            fontWeight: 700,
            color: "#344767",
          }}
        >
          {t('logout.dialog.title')}
        </DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          <DialogContentText
            id="logout-dialog-description"
            sx={{
              fontSize: "1rem",
              lineHeight: 1.5,
              color: "#7b809a",
              whiteSpace: "pre-line",
            }}
          >
            {t('logout.dialog.text')}
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setLogoutDialogOpen(false)}
            sx={{
              minWidth: 100,
              textTransform: "none",
              fontWeight: 500,
              color: "#344767",
              borderColor: "#344767",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "rgba(52, 71, 103, 0.04)",
                borderColor: "#344767",
                boxShadow: "none",
              },
              "&:focus": {
                boxShadow: "none",
              },
              "&:focus:not(:hover)": {
                boxShadow: "none",
              },
              "&:active": {
                boxShadow: "none",
              },
              "&.Mui-focusVisible": {
                outline: "2px solid #344767",
                outlineOffset: 2,
                boxShadow: "none",
              },
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="inherit"
            onClick={handleConfirmLogout}
            sx={{
              minWidth: 100,
              textTransform: "none",
              fontWeight: 500,
              backgroundColor: "#d32f2f",
              color: "#fff",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#b71c1c",
                boxShadow: "none",
              },
              "&:active": {
                backgroundColor: "#b71c1c",
                boxShadow: "none",
              },
              "&:focus": {
                backgroundColor: "#d32f2f",
                boxShadow: "none",
              },
              "&.Mui-focusVisible": {
                backgroundColor: "#b71c1c",
                boxShadow: "0 0 0 3px rgba(211, 47, 47, 0.45)",
              },
            }}
          >
            {t('logout.button')}
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop
        open={isLoggingOut}
        sx={{
          zIndex: theme.zIndex.modal + 10,
          flexDirection: "column",
          gap: 2,
          color: "#fff",
          backgroundColor: "rgba(20, 20, 30, 0.88)",
          backdropFilter: "blur(6px)",
        }}
      >
        <Fade in={isLoggingOut} timeout={{ enter: 400, exit: 200 }} style={{ outline: "none" }}>
          <div>
            <ArgonBox
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                animation: `${logoutPulse} 1.4s ease-in-out infinite`,
              }}
            >
              <CircularProgress color="inherit" size={52} thickness={3.5} />
              <Typography variant="h6" fontWeight={600} letterSpacing={0.3}>
                {t('logout.loading')}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.75, maxWidth: 280, textAlign: "center" }}>
                {t('logout.loadingDesc')}
              </Typography>
            </ArgonBox>
          </div>
        </Fade>
      </Backdrop>
    </ArgonBox>
  );
}

export default SidenavFooter;
